/**
 * Enrichment Progress Store (Zustand)
 *
 * Manages multiple concurrent enrichment operations across the app.
 * Supports HMC Events enrichment with extensibility for future operations.
 *
 * Features:
 * - Multiple concurrent operations (keyed by upload_id)
 * - Per-event progress tracking with phases
 * - Elapsed time tracking
 * - UI state (expanded/collapsed, position)
 * - Cancellation support
 *
 * Pattern follows: cacheStore.ts (SSE connection), routeStore.ts (progress tracking)
 */

import { create } from 'zustand';
import { logger } from '../utils/logger';

// ============================================================
// Types
// ============================================================

export type EnrichmentPhase =
  | 'idle'
  | 'connecting'
  | 'searching'   // Google Custom Search
  | 'found'       // Successfully enriched
  | 'skipped'     // No results found
  | 'failed';     // Error during enrichment

export type EnrichmentStatus =
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface EnrichmentEvent {
  id: number;
  name: string;
  phase: EnrichmentPhase;
  eventUrl?: string;
  youtubeUrl?: string;
  error?: string;
}

export interface EnrichmentOperation {
  id: string;               // upload_id for HMC
  type: 'hmc_enrichment';   // Extensible for future types
  status: EnrichmentStatus;

  // Progress
  currentIndex: number;
  totalCount: number;
  currentEvent: EnrichmentEvent | null;

  // Results
  enrichedCount: number;
  failedCount: number;
  skippedCount: number;

  // Timing
  startedAt: number;
  elapsedSeconds: number;

  // UI State
  isExpanded: boolean;
  isMinimized: boolean;

  // Error
  error: string | null;
}

interface EnrichmentProgressState {
  // All active operations
  operations: Record<string, EnrichmentOperation>;

  // Actions - Lifecycle
  startEnrichment: (uploadId: string, totalCount: number) => void;
  updateProgress: (uploadId: string, update: Partial<EnrichmentOperation>) => void;
  updateCurrentEvent: (uploadId: string, event: EnrichmentEvent) => void;
  completeEnrichment: (uploadId: string, results: { enriched: number; failed: number }) => void;
  failEnrichment: (uploadId: string, error: string) => void;
  cancelEnrichment: (uploadId: string) => void;
  dismissOperation: (uploadId: string) => void;

  // Actions - UI
  toggleExpanded: (uploadId: string) => void;
  toggleMinimized: (uploadId: string) => void;

  // Getters
  getActiveOperations: () => EnrichmentOperation[];
  hasActiveOperations: () => boolean;

  // Timer management
  _timers: Record<string, NodeJS.Timeout>;
}

// ============================================================
// Store
// ============================================================

export const useEnrichmentProgressStore = create<EnrichmentProgressState>((set, get) => ({
  operations: {},
  _timers: {},

  /**
   * Start a new enrichment operation
   */
  startEnrichment: (uploadId: string, totalCount: number) => {
    const now = Date.now();

    // Create new operation
    const operation: EnrichmentOperation = {
      id: uploadId,
      type: 'hmc_enrichment',
      status: 'running',
      currentIndex: 0,
      totalCount,
      currentEvent: null,
      enrichedCount: 0,
      failedCount: 0,
      skippedCount: 0,
      startedAt: now,
      elapsedSeconds: 0,
      isExpanded: true,
      isMinimized: false,
      error: null,
    };

    // Start elapsed time timer
    const timer = setInterval(() => {
      const state = get();
      const op = state.operations[uploadId];
      if (op && op.status === 'running') {
        set({
          operations: {
            ...state.operations,
            [uploadId]: {
              ...op,
              elapsedSeconds: Math.floor((Date.now() - op.startedAt) / 1000),
            },
          },
        });
      }
    }, 1000);

    set((state) => ({
      operations: {
        ...state.operations,
        [uploadId]: operation,
      },
      _timers: {
        ...state._timers,
        [uploadId]: timer,
      },
    }));

    logger.log(`[EnrichmentProgress] Started operation: ${uploadId} with ${totalCount} events`);
  },

  /**
   * Update progress for an operation
   */
  updateProgress: (uploadId: string, update: Partial<EnrichmentOperation>) => {
    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            ...update,
          },
        },
      };
    });
  },

  /**
   * Update the current event being processed
   */
  updateCurrentEvent: (uploadId: string, event: EnrichmentEvent) => {
    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            currentEvent: event,
          },
        },
      };
    });
  },

  /**
   * Complete an enrichment operation
   */
  completeEnrichment: (uploadId: string, results: { enriched: number; failed: number }) => {
    // Clear timer
    const timer = get()._timers[uploadId];
    if (timer) {
      clearInterval(timer);
    }

    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      const { [uploadId]: _, ...restTimers } = state._timers;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            status: 'completed',
            enrichedCount: results.enriched,
            failedCount: results.failed,
            currentEvent: null,
          },
        },
        _timers: restTimers,
      };
    });

    logger.log(`[EnrichmentProgress] Completed: ${uploadId} - ${results.enriched} enriched, ${results.failed} failed`);
  },

  /**
   * Mark an operation as failed
   */
  failEnrichment: (uploadId: string, error: string) => {
    // Clear timer
    const timer = get()._timers[uploadId];
    if (timer) {
      clearInterval(timer);
    }

    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      const { [uploadId]: _, ...restTimers } = state._timers;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            status: 'failed',
            error,
            currentEvent: null,
          },
        },
        _timers: restTimers,
      };
    });

    logger.error(`[EnrichmentProgress] Failed: ${uploadId} - ${error}`);
  },

  /**
   * Cancel an operation (UI side - also need to call backend)
   */
  cancelEnrichment: (uploadId: string) => {
    // Clear timer
    const timer = get()._timers[uploadId];
    if (timer) {
      clearInterval(timer);
    }

    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      const { [uploadId]: _, ...restTimers } = state._timers;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            status: 'cancelled',
            currentEvent: null,
          },
        },
        _timers: restTimers,
      };
    });

    logger.log(`[EnrichmentProgress] Cancelled: ${uploadId}`);
  },

  /**
   * Dismiss (remove) an operation from the UI
   */
  dismissOperation: (uploadId: string) => {
    // Clear timer if still running
    const timer = get()._timers[uploadId];
    if (timer) {
      clearInterval(timer);
    }

    set((state) => {
      const { [uploadId]: _, ...restOperations } = state.operations;
      const { [uploadId]: __, ...restTimers } = state._timers;

      return {
        operations: restOperations,
        _timers: restTimers,
      };
    });

    logger.log(`[EnrichmentProgress] Dismissed: ${uploadId}`);
  },

  /**
   * Toggle expanded state
   */
  toggleExpanded: (uploadId: string) => {
    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            isExpanded: !op.isExpanded,
          },
        },
      };
    });
  },

  /**
   * Toggle minimized state (pill vs panel)
   */
  toggleMinimized: (uploadId: string) => {
    set((state) => {
      const op = state.operations[uploadId];
      if (!op) return state;

      return {
        operations: {
          ...state.operations,
          [uploadId]: {
            ...op,
            isMinimized: !op.isMinimized,
          },
        },
      };
    });
  },

  /**
   * Get all active (non-dismissed) operations
   */
  getActiveOperations: () => {
    return Object.values(get().operations);
  },

  /**
   * Check if there are any active operations
   */
  hasActiveOperations: () => {
    return Object.keys(get().operations).length > 0;
  },
}));

// ============================================================
// Selectors (for use with useShallow)
// ============================================================

export const selectOperations = (state: EnrichmentProgressState) => state.operations;
export const selectHasActiveOperations = (state: EnrichmentProgressState) =>
  Object.keys(state.operations).length > 0;
